import React, { useState, useEffect } from 'react';
import { collection, getDocs, orderBy, query, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface LogEntry {
  id: string;
  timestamp: any;
  level: 'info' | 'warn' | 'error';
  message: string;
  userId?: string;
  action?: string;
}

interface AdminLogsProps {
  onBack: () => void;
}

const AdminLogs: React.FC<AdminLogsProps> = ({ onBack }) => {
  const { currentUser } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [filter, setFilter] = useState<'all' | 'info' | 'warn' | 'error'>('all');

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadLogs();
    }
  }, [currentUser]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading system logs...');

      // For now, we'll create some sample logs since we don't have a logging system yet
      // In a real implementation, you'd fetch from a logs collection
      const sampleLogs: LogEntry[] = [
        {
          id: '1',
          timestamp: new Date(Date.now() - 5 * 60 * 1000), // 5 minutes ago
          level: 'info',
          message: 'Admin user logged in successfully',
          userId: currentUser?.uid,
          action: 'login'
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 10 * 60 * 1000), // 10 minutes ago
          level: 'info',
          message: 'User statistics loaded successfully',
          action: 'stats_load'
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
          level: 'info',
          message: 'User management page accessed',
          userId: currentUser?.uid,
          action: 'page_access'
        },
        {
          id: '4',
          timestamp: new Date(Date.now() - 20 * 60 * 1000), // 20 minutes ago
          level: 'warn',
          message: 'Duplicate admin user detected and cleaned up',
          action: 'cleanup'
        },
        {
          id: '5',
          timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
          level: 'info',
          message: 'Firestore rules updated successfully',
          action: 'config_update'
        },
        {
          id: '6',
          timestamp: new Date(Date.now() - 45 * 60 * 1000), // 45 minutes ago
          level: 'error',
          message: 'Permission denied for user deletion (before rule update)',
          action: 'delete_user'
        }
      ];

      setLogs(sampleLogs);
      console.log('Loaded logs:', sampleLogs.length);
    } catch (error: any) {
      console.error('Error loading logs:', error);
      setError('Failed to load logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => 
    filter === 'all' || log.level === filter
  );

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'text-red-400 bg-red-900';
      case 'warn': return 'text-yellow-400 bg-yellow-900';
      case 'info': return 'text-blue-400 bg-blue-900';
      default: return 'text-gray-400 bg-gray-900';
    }
  };

  const formatTimestamp = (timestamp: any) => {
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
            <h2 className="text-xl font-bold">Access Denied</h2>
            <p>You must be an admin to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-700 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">System Logs</h1>
              <p className="text-purple-100">Monitor system activity and events</p>
            </div>
            <button
              onClick={onBack}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-md transition-all duration-200"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
            <p>{error}</p>
            <button
              onClick={() => setError('')}
              className="mt-2 text-red-200 hover:text-white underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Controls */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-300">Filter:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as 'all' | 'info' | 'warn' | 'error')}
                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1 text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="all">All Logs</option>
                <option value="info">Info</option>
                <option value="warn">Warning</option>
                <option value="error">Error</option>
              </select>
            </div>
            <button
              onClick={loadLogs}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-all duration-200"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Loading logs...</p>
          </div>
        )}

        {/* Logs Table */}
        {!loading && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <h2 className="text-xl font-semibold">
                Recent Activity ({filteredLogs.length} entries)
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Message
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-750">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor(log.level)}`}>
                          {log.level.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {log.message}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {log.action || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredLogs.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                <p>No logs found for the selected filter.</p>
              </div>
            )}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 bg-blue-900 bg-opacity-30 border border-blue-500 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-300 mb-2">About System Logs</h3>
          <p className="text-blue-100 text-sm">
            This is a sample logs page showing recent system activity. In a production environment, 
            you would integrate with a proper logging system like Cloud Logging, Winston, or similar.
            The logs shown here are examples of the types of events that would be tracked.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogs;












